/**
 * formatArtworkName -- single-source display formatter for artwork names.
 *
 * Stored artwork names carry a GUID-style identity suffix after the first
 * '#', e.g. "TL-logo-White#e43d3a8912c4356b5b27f847f50". That suffix is
 * machinery (identity / lookup) and must never be shown to the user.
 * This helper strips everything from the FIRST '#' onward and trims the
 * result, leaving just the human-readable name.
 *
 * DISPLAY ONLY: never route stored values, keys, testids, or anything used
 * for identity/lookup through a mutated copy -- format at render time only.
 *
 * Contract:
 *   - "Name#guid"     -> "Name"
 *   - "PlainName"     -> "PlainName" (no '#' -> unchanged, trimmed)
 *   - "a#b#c"         -> "a"         (cut at the FIRST '#')
 *   - "#guid"         -> ""          (leading '#' -> empty)
 *   - "" / null /
 *     undefined /
 *     non-string       -> ""         (safe fallback)
 *
 * @param {*} raw the raw stored name (may be null/undefined/non-string)
 * @returns {string} the GUID-stripped, trimmed display name
 */
export function formatArtworkName(raw) {
    if (typeof raw !== "string") return "";
    return cutAtIdentitySuffix(raw, 0).trim();
}

/**
 * artworkPayloadName -- the PERSISTED-payload variant of the same rule.
 *
 * Used where the stripped name is emitted as data, not just shown
 * (EditorArtworkPicker's `picked.artworkFilename`). Same core cut as
 * formatArtworkName, with two deliberate differences that keep the stored
 * payload byte-stable:
 *   - a LEADING '#' (hash at index 0) is NOT a suffix: the raw value is kept
 *     (formatArtworkName returns "" there);
 *   - no trim: a name without '#' is returned verbatim.
 * Non-strings -> "". Never use this for display; never use
 * formatArtworkName for a payload.
 *
 * @param {*} raw the raw stored name
 * @returns {string}
 */
export function artworkPayloadName(raw) {
    if (typeof raw !== "string") return "";
    return cutAtIdentitySuffix(raw, 1);
}

// One implementation core: cut at the FIRST '#' when it sits at index
// >= minIndex; otherwise return the string unchanged.
function cutAtIdentitySuffix(raw, minIndex) {
    const hashIndex = raw.indexOf("#");
    return hashIndex >= minIndex && hashIndex !== -1
        ? raw.slice(0, hashIndex)
        : raw;
}

export default formatArtworkName;
